import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import quizApi from '../utils/quizApi';
import DocKeyLink from './DocKeyLink';
import '../styles/Quiz.css';

/**
 * Choose the document a quiz attaches to, from the training report.
 *
 * Not a text box. A typed key can be wrong, and a wrong key produces a quiz that
 * is delivered to nobody while looking entirely correct on the list — the exact
 * shape of failure this product exists to surface. Picking from the report also
 * means the revision and the name come from the same place the compliance grid
 * reads them, so the two can never disagree.
 *
 * Documents that CAN'T take a quiz are listed anyway, greyed, with the reason.
 * Hiding them would turn "why can't I find DMS-30114?" into a mystery; showing
 * the reason answers it on the spot.
 *
 * **Paged, and the search runs on the SERVER.** A controlled library is unbounded
 * — it is whatever the tenant's DMS filter returns — so fetching it whole and
 * filtering here was the same shape of bug as any other unbounded read in this
 * codebase: invisible on a 55-document tenant, unusable on a large one. Searching
 * locally would be worse than not searching at all once the list is paged, because
 * it would silently only ever search the page you are looking at.
 */
const PAGE_SIZE = 25;

function DocumentPicker({ onPick, onCancel, setupId = '' }) {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);        // a refetch, with rows still on screen
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [showBlocked, setShowBlocked] = useState(false);
  const [page, setPage] = useState(1);

  // Typing must not fire a request per keystroke; 300 ms is long enough to be one
  // request per word and short enough to feel like local filtering.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  // A new search or a toggled filter re-pages from 1 — page 3 of the old result set
  // is a different set of rows, and landing past the end reads as "nothing found".
  useEffect(() => { setPage(1); }, [debounced, showBlocked]);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    (async () => {
      try {
        const d = await quizApi.documents({
          setup_id: setupId, q: debounced, page, limit: PAGE_SIZE,
          show_blocked: showBlocked ? 'true' : '',
        });
        if (!cancelled) { setState(d); setError(''); }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) { setLoading(false); setBusy(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [setupId, debounced, showBlocked, page]);

  const rows = state?.documents || [];
  const total = state?.total || 0;
  const blockedCount = state?.blocked_count || 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (loading) return <div className="qz-loading">Loading documents…</div>;
  // Only take the whole screen when there is nothing to fall back to. A failed
  // *re*fetch leaves the last good page on screen with the error above it — losing
  // the list because one search request failed is a worse answer than a stale page.
  if (error && !state) return <div className="qz-note qz-note-err"><span>{error}</span></div>;

  if (state?.state === 'no_snapshot') {
    return (
      <div className="qz-empty">
        <b>The training report hasn’t been built yet</b>
        <span>
          Quizzes attach to the documents in a training panel’s report, so there is nothing
          to choose from until one has been built. Open a training panel and build its report,
          then come back.
        </span>
        <button className="qz-btn qz-btn-ghost" onClick={() => navigate('/training')}>
          Go to training panels
        </button>
      </div>
    );
  }

  return (
    <div>
      {error && <div className="qz-note qz-note-err"><span>{error}</span></div>}
      <div className="qz-toolbar">
        <input
          className="qz-search"
          placeholder="Search by key, name or role…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
        {blockedCount > 0 && (
          <button
            className={`qz-chip${showBlocked ? ' active' : ''}`}
            onClick={() => setShowBlocked((v) => !v)}
          >
            {showBlocked ? 'Hide' : 'Show'} {blockedCount} that can’t take a quiz
          </button>
        )}
        {/* The count is of MATCHING documents, not of the library — it is the number
            the pager is walking, so the two can never tell different stories. */}
        <span className="qz-count">
          {busy ? 'Searching…'
            : `${total} document${total === 1 ? '' : 's'}${debounced ? ' match' : ''}`}
        </span>
      </div>

      {rows.length === 0 ? (
        <div className="qz-empty">
          <b>Nothing matches</b>
          <span>
            {debounced
              ? 'No document in the report matches that search.'
              : 'Every document in the report already has a quiz, or can’t take one.'}
          </span>
        </div>
      ) : (
        <div className="qz-tablewrap">
          <table className="qz-table">
            <thead>
              <tr>
                <th>Document</th>
                <th>Revision</th>
                <th>Audience</th>
                <th>Quiz</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.key} className={d.blocked ? 'qz-blocked' : ''}>
                  <td>
                    <div className="qz-name">{d.name || d.key}</div>
                    <div className="qz-task-meta">
                      <DocKeyLink docKey={d.key} url={d.url} />
                      {d.src === 'formal' && <span className="qz-pill qz-pill-formal">Formal</span>}
                      {d.approved_on && (
                        <span>
                          {d.date_kind === 'completed' ? 'completed' : 'approved'} {d.approved_on}
                        </span>
                      )}
                    </div>
                    {d.blocked && (
                      <div className="qz-blocked-why">{d.blocked_reason}</div>
                    )}
                  </td>
                  <td>
                    {d.revision
                      ? <span className="qz-pill qz-pill-rev">{d.revision}</span>
                      : <span className="qz-meta">
                          {d.src === 'formal' ? 'No revision' : '—'}
                        </span>}
                  </td>
                  <td>
                    {d.people
                      ? <>
                          <div>{d.people} {d.people === 1 ? 'person' : 'people'}</div>
                          {d.roles.length > 0 && (
                            <div className="qz-meta">{d.roles.join(' · ')}</div>
                          )}
                        </>
                      : <span className="qz-meta">Nobody</span>}
                  </td>
                  <td>
                    {d.quiz ? (
                      <>
                        <span className={`qz-pill ${d.quiz.status === 'published'
                          ? 'qz-pill-live' : 'qz-pill-draft'}`}>
                          {d.quiz.status === 'published' ? 'Published' : 'Draft'}
                        </span>
                        {d.quiz.has_attempts && (
                          <div className="qz-meta">In use — frozen</div>
                        )}
                      </>
                    ) : d.stale_quiz ? (
                      /* There IS a quiz — on a revision this document has moved past,
                         so it reaches nobody. Saying "None" here is how an author ends
                         up writing a second quiz from scratch and never learning the
                         first one went dark. */
                      <>
                        <span className="qz-pill qz-pill-open">Revision {d.stale_quiz.revision || '—'}</span>
                        <div className="qz-blocked-why">Reaching nobody</div>
                      </>
                    ) : <span className="qz-meta">None</span>}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {d.stale_quiz && !d.quiz ? (
                      <button
                        className="qz-btn qz-btn-ghost qz-btn-sm"
                        onClick={() => navigate(`/training/quizzes/${d.stale_quiz.id}`)}
                      >Carry forward →</button>
                    ) : d.quiz ? (
                      /* Already has one: the useful action is to OPEN it, not to be
                         told no. This is the same message the API sends on a create
                         attempt, offered before the person can hit it. */
                      <button
                        className="qz-btn qz-btn-ghost qz-btn-sm"
                        onClick={() => navigate(`/training/quizzes/${d.quiz.id}`)}
                      >Open quiz →</button>
                    ) : d.blocked ? (
                      <span className="qz-meta">Can’t add</span>
                    ) : (
                      <button
                        className="qz-btn qz-btn-primary qz-btn-sm"
                        onClick={() => onPick(d)}
                      >Add quiz</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="qz-pager">
          <button className="qz-btn qz-btn-ghost qz-btn-sm"
                  disabled={page <= 1 || busy}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}>← Previous</button>
          <span>Page {page} of {pages}</span>
          <button className="qz-btn qz-btn-ghost qz-btn-sm"
                  disabled={page >= pages || busy}
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}>Next →</button>
        </div>
      )}

      {onCancel && (
        <div className="qz-take-foot">
          <span className="qz-spacer" />
          <button className="qz-btn qz-btn-ghost" onClick={onCancel}>Cancel</button>
        </div>
      )}
    </div>
  );
}

export default DocumentPicker;
